/**
 * Per-document student verification.
 *
 * `student_profiles.verified` is a ROW-level flag — it only says "the last
 * admin action on this profile was an approval" — so it cannot answer "is the
 * HKID approved?". The admin has been approving each document separately since
 * the backend split the write (`apps/uninfo-api/src/config/studentVerificationDocs.js`),
 * and the evidence for that lives in the per-document columns:
 *
 *   - `student_card_number` / `student_card_name` (and the HKID pair) are set
 *     when an admin reviewed THAT document — the backend's badge rule.
 *   - `verified_student_card_image_url` / `verified_hkid_image_url` snapshot the
 *     image the admin actually looked at — the evidence that THIS document was
 *     reviewed.
 *
 * Deriving "verified" from the row-level flag alone is what made a student
 * whose student card was approved but whose HKID is still under review read as
 * "Verified" on both rows, and read "Your identity has been verified." This
 * module mirrors the backend's derivation exactly so the student-facing card
 * and the admin console can never disagree about what is approved.
 *
 * Empty string is not absence: 159 of 1002 live profiles store
 * `student_card_image_url = ""`, so every presence test excludes "" as well as
 * NULL — the same rule the backend applies.
 *
 * Pure on purpose — no React, no i18n, no side effects — so the rules can be
 * unit tested without a renderer and shared with the mobile build verbatim.
 */

/** The two documents a student has to hand in. */
export type StudentVerificationDocument = "student_card" | "hkid";

/**
 * A value that may or may not be on file. The backend stores these as
 * nullable columns, so `null`, `undefined` and `""` all mean "absent".
 */
export type StudentVerificationFieldValue =
  | string
  | number
  | boolean
  | Date
  | null
  | undefined;

/**
 * The subset of a `student_profiles` row this module reads. Every field is
 * optional so a partially loaded profile (or `null`) is still a valid input.
 */
export interface StudentProfileVerification {
  student_card_image_url?: StudentVerificationFieldValue;
  hkid_image_url?: StudentVerificationFieldValue;
  student_card_number?: StudentVerificationFieldValue;
  student_card_name?: StudentVerificationFieldValue;
  hkid_number?: StudentVerificationFieldValue;
  hkid_name?: StudentVerificationFieldValue;
  verified_student_card_image_url?: StudentVerificationFieldValue;
  verified_hkid_image_url?: StudentVerificationFieldValue;
  verified?: StudentVerificationFieldValue;
  rejected_at?: StudentVerificationFieldValue;
}

/** The state of both documents, as the student sees it. */
export interface StudentVerificationState {
  /** The student card image is on file. */
  cardUploaded: boolean;
  /** The HKID image is on file. */
  hkidUploaded: boolean;
  /** The student card was reviewed and approved. */
  cardVerified: boolean;
  /** The HKID was reviewed and approved. */
  hkidVerified: boolean;
  /** The student card may be (re-)uploaded. */
  cardNeedsUpload: boolean;
  /** The HKID may be (re-)uploaded. */
  hkidNeedsUpload: boolean;
  /** An admin rejected this profile; the documents have to be sent again. */
  rejected: boolean;
  /** Both documents are approved. */
  fullyVerified: boolean;
  /** At least one document is on file. */
  anyUploaded: boolean;
}

/** The header badge of the card. */
export type StudentVerificationBadgeKey =
  | "Rejected"
  | "Verified"
  | "Pending approval"
  | "Not Verified";

/** The subtitle under the card title — an i18n key, never prose. */
export type StudentVerificationSubtitleKey =
  | "Your identity has been verified."
  | "Upload your HKID to redeem coupons and sponsors."
  | "Upload your student card to join activities and redeem."
  | "Your documents are being reviewed by an admin."
  | "Verify your identity to redeem coupons, sponsors and join activities.";

/** The subtitle and whether it has to be shown as a call to action. */
export interface StudentVerificationSubtitle {
  key: StudentVerificationSubtitleKey;
  /** True when the student still has to do something (orange notice). */
  notice: boolean;
}

/** What a document row shows on its right-hand side. */
export type StudentVerificationDocumentStatus =
  | "verified"
  | "uploaded"
  | "needs-upload";

/**
 * The column each piece of evidence lives in, per document. `approvedImage` is
 * the snapshot of the image an admin actually reviewed — the upload endpoint
 * overwrites `image` in place, so without the snapshot the evidence behind a
 * review cannot be proven later.
 */
const DOCUMENT_FIELDS: Record<
  StudentVerificationDocument,
  {
    image: keyof StudentProfileVerification;
    number: keyof StudentProfileVerification;
    name: keyof StudentProfileVerification;
    approvedImage: keyof StudentProfileVerification;
  }
> = {
  student_card: {
    image: "student_card_image_url",
    number: "student_card_number",
    name: "student_card_name",
    approvedImage: "verified_student_card_image_url",
  },
  hkid: {
    image: "hkid_image_url",
    number: "hkid_number",
    name: "hkid_name",
    approvedImage: "verified_hkid_image_url",
  },
};

/**
 * Is this value on file?
 *
 * Mirrors the backend's `present`: `""` (and whitespace only) is absence, and
 * so is `null`/`undefined`. Every other value — including `false` and `0` —
 * counts as present, which is what the backend does.
 */
export const present = (value: StudentVerificationFieldValue): boolean =>
  typeof value === "string"
    ? value.trim().length > 0
    : value !== null && value !== undefined;

/** Did the student actually submit an image for this document? */
export const isDocumentUploaded = (
  profile: StudentProfileVerification | null | undefined,
  document: StudentVerificationDocument,
): boolean => present(profile?.[DOCUMENT_FIELDS[document].image]);

/**
 * Badge derivation, unchanged from the backend: a document counts as verified
 * once both its number and its name are on file.
 */
export const isDocumentVerified = (
  profile: StudentProfileVerification | null | undefined,
  document: StudentVerificationDocument,
): boolean =>
  present(profile?.[DOCUMENT_FIELDS[document].number]) &&
  present(profile?.[DOCUMENT_FIELDS[document].name]);

/**
 * Evidence derivation: the approval snapshot exists, so THIS document was
 * reviewed by an admin.
 */
export const isDocumentApproved = (
  profile: StudentProfileVerification | null | undefined,
  document: StudentVerificationDocument,
): boolean => present(profile?.[DOCUMENT_FIELDS[document].approvedImage]);

/**
 * The whole verification state of a profile.
 *
 * The upload rule this produces:
 *   - a document that is verified can NEVER be uploaded again;
 *   - a document that is merely on file (pending) cannot be uploaded either;
 *   - only a missing document — or one an admin rejected — can be uploaded.
 */
export const getStudentVerificationState = (
  profile?: StudentProfileVerification | null,
): StudentVerificationState => {
  const cardUploaded = isDocumentUploaded(profile, "student_card");
  const hkidUploaded = isDocumentUploaded(profile, "hkid");

  // Only per-document evidence approves a document. The row-level `verified`
  // flag is deliberately ignored: it is set by ANY admin approval, so using it
  // would mark a freshly uploaded document as verified before it was reviewed.
  const cardVerified =
    isDocumentVerified(profile, "student_card") ||
    isDocumentApproved(profile, "student_card");
  const hkidVerified =
    isDocumentVerified(profile, "hkid") ||
    isDocumentApproved(profile, "hkid");

  const rejected = present(profile?.rejected_at);

  return {
    cardUploaded,
    hkidUploaded,
    cardVerified,
    hkidVerified,
    cardNeedsUpload: !cardVerified && (!cardUploaded || rejected),
    hkidNeedsUpload: !hkidVerified && (!hkidUploaded || rejected),
    rejected,
    fullyVerified: cardVerified && hkidVerified,
    anyUploaded: cardUploaded || hkidUploaded,
  };
};

/**
 * The header badge. A rejection wins over everything: the student has to send
 * the documents again, so the profile is not "Verified" any more.
 */
export const getStudentVerificationBadge = (
  state: StudentVerificationState,
): StudentVerificationBadgeKey => {
  if (state.rejected) return "Rejected";
  if (state.fullyVerified) return "Verified";
  if (state.anyUploaded) return "Pending approval";
  return "Not Verified";
};

/**
 * The subtitle under the card title.
 *
 * It names the document that is still missing, so the student knows what to do
 * next. `notice` is true only when the student still has to do something.
 */
export const getStudentVerificationSubtitle = (
  state: StudentVerificationState,
): StudentVerificationSubtitle => {
  if (state.rejected) {
    return {
      key: "Verify your identity to redeem coupons, sponsors and join activities.",
      notice: true,
    };
  }
  if (state.fullyVerified) {
    return { key: "Your identity has been verified.", notice: false };
  }
  if (state.cardVerified && !state.hkidVerified) {
    return state.hkidUploaded
      ? {
          key: "Your documents are being reviewed by an admin.",
          notice: false,
        }
      : {
          key: "Upload your HKID to redeem coupons and sponsors.",
          notice: true,
        };
  }
  if (state.hkidVerified && !state.cardVerified) {
    return state.cardUploaded
      ? {
          key: "Your documents are being reviewed by an admin.",
          notice: false,
        }
      : {
          key: "Upload your student card to join activities and redeem.",
          notice: true,
        };
  }
  return state.anyUploaded
    ? {
        key: "Your documents are being reviewed by an admin.",
        notice: false,
      }
    : {
        key: "Verify your identity to redeem coupons, sponsors and join activities.",
        notice: true,
      };
};

/**
 * What a document row shows: the upload button (when the document still needs
 * one), the "Verified" pill, or the "Uploaded" pill.
 */
export const getStudentDocumentStatus = (
  state: StudentVerificationState,
  document: StudentVerificationDocument,
): StudentVerificationDocumentStatus => {
  const isHkid = document === "hkid";
  const verified = isHkid ? state.hkidVerified : state.cardVerified;
  const uploaded = isHkid ? state.hkidUploaded : state.cardUploaded;
  const needsUpload = isHkid ? state.hkidNeedsUpload : state.cardNeedsUpload;
  if (needsUpload) return "needs-upload";
  return verified ? "verified" : "uploaded";
};
